import type {ClipboardEvent,DragEvent,FormEvent,HTMLAttributes,KeyboardEvent,MouseEvent,RefCallback} from 'react';

type NumericKind='number'|'decimal'|'digits';
type GuardEvent={target:EventTarget|null;defaultPrevented?:boolean;preventDefault:()=>void;nativeEvent?:Event};
function kind(input:HTMLInputElement):NumericKind|null{
 if(input.dataset.dateInput==='true')return null;
 if(input.type==='number')return 'number';
 if(input.inputMode==='decimal')return 'decimal';
 if(input.inputMode==='numeric')return 'digits';
 return null;
}
function target(event:GuardEvent){
 const input=event.target;
 return typeof HTMLInputElement!=='undefined'&&input instanceof HTMLInputElement&&kind(input)&&!input.disabled&&!input.readOnly?input:null;
}
function signed(input:HTMLInputElement){const min=input.getAttribute('min');return kind(input)!=='digits'&&(min===null||min===''||!Number.isFinite(Number(min))||Number(min)<0);}
/** Editing prefixes are intentional; native min/max/step validity still gates submit. */
export function numericDraftAllowed(value:string,input:HTMLInputElement){
 if(kind(input)==='digits')return /^\d*$/.test(value);
 if(input.dataset.wholeNumber==='true'||input.type==='number'&&input.getAttribute('step')==='1')return (signed(input)?/^-?\d*$/:/^\d*$/).test(value);
 return (signed(input)?/^-?\d*(?:\.\d{0,4})?$/:/^\d*(?:\.\d{0,4})?$/).test(value);
}
function insertionAllowed(input:HTMLInputElement,data:string){
 if(!numericDraftAllowed(data,input))return false;
 // Text decimal inputs expose the selection; number inputs deliberately do not.
 const start=input.selectionStart,end=input.selectionEnd;
 return start===null||end===null||numericDraftAllowed(input.value.slice(0,start)+data+input.value.slice(end),input);
}
export function guardNumericKeyDown(event:KeyboardEvent<HTMLElement>){
 const input=target(event);if(!input||event.defaultPrevented||event.ctrlKey||event.metaKey||event.altKey||event.nativeEvent.isComposing)return;
 if(event.key.length===1&&!numericDraftAllowed(event.key,input))event.preventDefault();
}
export function guardNumericBeforeInput(event:GuardEvent){
 const input=target(event);if(!input||event.defaultPrevented)return;
 const native=(event.nativeEvent??event)as InputEvent;
 if(native.inputType?.startsWith('delete'))return;
 if(typeof native.data==='string'&&!insertionAllowed(input,native.data))event.preventDefault();
}
export function guardNumericPaste(event:ClipboardEvent<HTMLElement>){
 const input=target(event);if(input&&!event.defaultPrevented&&!insertionAllowed(input,event.clipboardData.getData('text/plain')))event.preventDefault();
}
export function guardNumericDrop(event:DragEvent<HTMLElement>){
 const input=target(event);if(input&&!event.defaultPrevented&&!insertionAllowed(input,event.dataTransfer.getData('text/plain')))event.preventDefault();
}
/** Fallback for replacements/autofill that bypass a cancelable beforeinput event. */
export function numericChangeAllowed(input:HTMLInputElement,event?:Event){
 if(!kind(input)||input.disabled||input.readOnly)return true;
 if(!numericDraftAllowed(input.value,input))return !!(event as InputEvent|undefined)?.inputType?.startsWith('delete');
 if(input.type!=='number'||!input.validity.badInput)return true;
 const native=event as InputEvent|undefined;
 if(native?.inputType?.startsWith('delete'))return true;
 // A bare sign/dot is an editable number prefix, not an accepted business value.
 return typeof native?.data==='string'&&['-','.'].includes(native.data)&&numericDraftAllowed(native.data,input);
}
function guardNumericChange(event:FormEvent<HTMLElement>){
 const input=target(event);if(input&&!numericChangeAllowed(input,event.nativeEvent)){event.preventDefault();event.stopPropagation();}
}
// Select an existing value only when a mouse click first focuses the field.
// Subsequent clicks retain normal caret placement for editing individual digits.
const firstMouseFocus=new WeakMap<HTMLInputElement,{x:number;y:number}>();
function numericMouseDown(event:MouseEvent<HTMLElement>){
 const input=target(event);if(!input)return;
 firstMouseFocus.delete(input);
 if(event.button===0&&!event.shiftKey&&!event.ctrlKey&&!event.metaKey&&!event.altKey&&document.activeElement!==input)firstMouseFocus.set(input,{x:event.clientX,y:event.clientY});
}
function numericMouseUp(event:MouseEvent<HTMLElement>){
 const input=target(event);if(!input)return;const start=firstMouseFocus.get(input);if(!start)return;
 firstMouseFocus.delete(input);
 if(document.activeElement===input&&input.value&&Math.abs(event.clientX-start.x)<5&&Math.abs(event.clientY-start.y)<5){event.preventDefault();input.select();}
}
export const numericInputCaptureHandlers:Pick<HTMLAttributes<HTMLElement>,'onKeyDownCapture'|'onBeforeInputCapture'|'onPasteCapture'|'onDropCapture'|'onChangeCapture'|'onMouseDownCapture'|'onMouseUpCapture'>={
 onKeyDownCapture:guardNumericKeyDown,
 onBeforeInputCapture:guardNumericBeforeInput,
 onPasteCapture:guardNumericPaste,
 onDropCapture:guardNumericDrop,
 onChangeCapture:guardNumericChange,
 onMouseDownCapture:numericMouseDown,
 onMouseUpCapture:numericMouseUp
};
/** React's beforeinput shim misses some native/mobile replacements. No values are mutated. */
export const numericInputScopeRef:RefCallback<HTMLElement>=node=>{
 if(!node)return;
 const before=(event:Event)=>guardNumericBeforeInput(event);
 node.addEventListener('beforeinput',before,true);
 return()=>node.removeEventListener('beforeinput',before,true);
};
